import { Button } from "@/src/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTrigger } from "@/src/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/src/components/ui/form";
import { Input } from "@/src/components/ui/input";
import { InputNumber } from "@/src/components/ui/input-number";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/src/components/ui/select";
import { useToast } from "@/src/components/ui/use-toast";
import { useAsyncList } from "@/src/lib/useAsyncList";
import { useFetch } from "@/src/lib/useFetch";
import { zodResolver } from "@hookform/resolvers/zod";
import { Separator } from "@radix-ui/react-separator";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const schema = z.object({
  from: z.string(),
  to: z.string(),
  amount: z.number(),
});
type CalculatorInput = z.infer<typeof schema>;

export function CalculatorButton() {
  const currencyCodes = useAsyncList<string>("/api/currencies/codes");

  const [result, setResult] = useState<string>("");
  const { toast } = useToast();
  const { fetch, isFetching } = useFetch();

  const form = useForm<CalculatorInput>({
    resolver: zodResolver(schema),
    defaultValues: {
      from: "",
      to: "",
      amount: 100,
    },
  });

  const onSubmit = form.handleSubmit(async (data) => {
    try {
      const response = await fetch<{ amount: number }>(
        `/api/currencies/price?from=${data.from}&to=${data.to}&amount=${data.amount}`,
      );
      const intl = new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: data.to,
      });
      setResult(intl.format(response.amount));
    } catch (error: any) {
      const message = "message" in error ? error.message : "An error occurred";
      toast({
        title: "Failed to calculate",
        description: message,
        variant: "destructive",
      });
    }
  });

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button>Calculator</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <h2 className="text-3xl">Calculator</h2>
        </DialogHeader>
        <Separator />
        <Form {...form}>
          <form className="flex flex-col gap-6" onSubmit={onSubmit}>
            <FormField<CalculatorInput, "from">
              name="from"
              render={({ field: { value, onChange } }) => (
                <FormItem>
                  <FormLabel>From</FormLabel>
                  <Select onValueChange={onChange} defaultValue={value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {currencyCodes.map((code) => (
                        <SelectItem key={code} value={code}>
                          {code}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
            <FormField<CalculatorInput, "to">
              name="to"
              render={({ field: { value, onChange } }) => (
                <FormItem>
                  <FormLabel>To</FormLabel>
                  <Select onValueChange={onChange} defaultValue={value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {currencyCodes.map((code) => (
                        <SelectItem key={code} value={code}>
                          {code}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
            <FormField<CalculatorInput, "amount">
              name="amount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Amount</FormLabel>
                  <FormControl>
                    <InputNumber {...field} />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormItem>
              <FormLabel>Result</FormLabel>
              <FormControl>
                <Input readOnly value={result} />
              </FormControl>
            </FormItem>

            <Button disabled={isFetching}>Calculate</Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
